import styled, { css } from 'styled-components';

import { getStatusColor } from '@/components/Card/Card.helpers';
import { Button } from '@/components/ui/Button/Button';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { CardStatus } from '@/constants/common';
import {
  ellipsisText,
  flexBetween,
  flexCenterAligned,
  flexColumn
} from '@/styles/common';

export const CardWrapper = styled.div<{
  $isFeatured: boolean;
  $isMobile: boolean;
  $isDeactivated: boolean;
  $isLink: boolean;
  $isResume?: boolean;
}>`
  ${({ $isDeactivated, $isLink }) =>
    !$isDeactivated && $isLink && 'cursor: pointer;'}
  width: 100%;
  background-color: ${({ $isResume }) =>
    $isResume ? 'var(--bg-resumeCard)' : 'var(--bg-card)'};
  border-radius: 28px;
  padding: 19px 19px 14px;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 15px;
  color: var(--textAndLabels-main);
  border: 1px solid var(--border-secondary);

  ${({ $isMobile }) =>
    $isMobile ? 'max-height: 294px; height: 100%' : 'min-height: 294px;'};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    height: auto;
    padding: 14px 19px 19px;
  }
`;

export const Header = styled.div<{
  $isHeaderVisible: boolean;
}>`
  ${flexBetween}
  padding: 7px 0px;
  display: ${({ $isHeaderVisible }) => ($isHeaderVisible ? 'flex' : 'none')};
`;

export const AddToBookmarksButton = styled.button<{
  $isBookmarked: boolean;
  $isDeactivated: boolean;
  $isVisible: boolean;
}>`
  ${({ $isVisible }) => ($isVisible ? flexCenterAligned : 'display: none;')}
  width: 22px;
  height: 22px;
  background: none;
  border: none;
  ${({ $isDeactivated }) => !$isDeactivated && 'cursor: pointer;'}

  svg {
    transition: all 0.3s ease;
    color: ${({ $isBookmarked, $isDeactivated }) =>
      $isDeactivated
        ? 'var(--textAndLabels-tertiary)'
        : $isBookmarked
          ? 'var(--system-red)'
          : 'var(--textAndLabels-main)'};

    fill: ${({ $isBookmarked }) =>
      $isBookmarked ? 'var(--system-red)' : 'var(--bookmark-fill)'};
  }
  ${({ $isDeactivated, $isBookmarked }) =>
    !$isDeactivated &&
    css`
      &:hover {
        svg {
          fill: ${$isBookmarked
            ? 'var(--system-red)'
            : 'var(--bookmark-hover-fill)'};
        }
      }
    `}
`;

export const PublishedAtText = styled(Typography)`
  color: var(--textAndLabels-tertiary);
`;

export const Status = styled(Typography).attrs({ variant: 'caption1' })<{
  $status: CardStatus;
}>`
  color: ${({ $status }) => getStatusColor($status)};
`;

export const Content = styled.div<{
  $isHeaderVisible: boolean;
  $isPublishPreview?: boolean;
}>`
  overflow: hidden;
  border-bottom: 1px solid var(--border-secondary);

  ${flexColumn}

  padding-bottom: ${({ $isPublishPreview, $isHeaderVisible }) =>
    $isPublishPreview ? '20px' : $isHeaderVisible ? '14px' : '20px'};
`;

export const InfoBlock = styled.div<{ $isDeactivated: boolean }>`
  display: flex;
  justify-content: space-between;
  overflow: hidden;
  gap: 10px;
  margin-bottom: 15px;
  flex-shrink: 0;
  opacity: ${({ $isDeactivated }) => ($isDeactivated ? '0.5' : '1')};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-bottom: 10px;
  }
`;

export const MainInfo = styled.div`
  overflow: hidden;
`;

export const Subtitle = styled(Typography)`
  display: -webkit-box;
  max-width: 100%;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  ${ellipsisText}
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 5px;

  & > svg {
    flex-shrink: 0;
  }
`;

export const Tags = styled.div<{ $isDeactivated: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  color: ${({ $isDeactivated }) =>
    $isDeactivated
      ? 'var(--textAndLabels-tertiary)'
      : 'var(--textAndLabels-main)'};
`;

export const Actions = styled.div<{ $extraPadding: boolean }>`
  display: flex;
  gap: 5px;
  padding: ${({ $extraPadding }) => ($extraPadding ? '16px 0' : '0')};

  height: 100%;
  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${({ $extraPadding }) => ($extraPadding ? '15px 0 0 0' : '0')};
  }
`;

export const ActivateButton = styled(Button).attrs({
  variant: 'primary',
  size: 's',
  fullWidth: true
})`
  background-color: var(--system-deepBlue);
  color: var(--bg-main);

  &:hover {
    background-color: var(--system-blue);
  }
`;

export const PromoteWrapper = styled.div`
  ${flexColumn}
  width: 100%;
  gap: 10px;
`;

export const PromoteButton = styled(Button).attrs({
  variant: 'primary',
  size: 's',
  fullWidth: true
})`
  background-color: var(--system-black);
  color: var(--promote-button-text);
  padding: 11px 18px 11px 13px;

  &:hover {
    background-color: #000;
  }
`;

export const Footer = styled.div`
  ${flexBetween}
  height: 30px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    height: auto;
  }
`;

export const Salary = styled(Typography)<{ $isDeactivated: boolean }>`
  color: ${({ $isDeactivated }) =>
    $isDeactivated
      ? 'var(--textAndLabels-tertiary)'
      : 'var(--textAndLabels-main)'};
`;

export const ChipsWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const commonChipStyles = css`
  ${flexCenterAligned}
  width: 30px;
  height: 30px;
  border-radius: 50px;
`;

export const PremiumChip = styled.div`
  ${commonChipStyles};
  background-color: #c8ef01;
  color: var(--title-snackbar);
`;

export const BumpChip = styled.div`
  ${commonChipStyles};
  background-color: var(--bg-card);
  border: 1px solid var(--textAndLabels-main);
`;

export const DeactivatedChip = styled.div`
  ${commonChipStyles};
  width: 79px;
  background-color: var(--bg-card);
  border: 1px solid var(--border-main);
`;

export const MenuWrapper = styled.div`
  position: relative;
`;

export const MenuButton = styled.button<{ $isOpen: boolean }>`
  ${flexCenterAligned}
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 28px;
  border: 1px solid
    ${({ $isOpen }) =>
      $isOpen ? 'var(--textAndLabels-main)' : 'var(--border-main)'};
  color: var(--textAndLabels-main);
  transition: all 0.3s ease;

  ${({ $isOpen }) =>
    !$isOpen &&
    css`
      &:hover {
        color: var(--custom-yellow);
        border-color: var(--custom-yellow);
      }
    `}
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: var(--bg-main);
  border-radius: 28px;
  box-shadow: 0px 17px 34px 0px rgba(151, 151, 151, 0.08);
  z-index: 1;
  width: 240px;
  padding: 10px 5px;
`;

export const DropdownItem = styled.div`
  padding: 13px 15px;
  cursor: pointer;
  color: var(--textAndLabels-main);
  transition: color 0.3s ease;

  &:hover {
    color: var(--custom-yellow);
  }
`;

export const DeleteModalActions = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ProgressBarWrapper = styled.div`
  ${flexColumn}
  gap: 10px;
  color: var(--textAndLabels-tertiary);
`;

export const ProgressBar = styled.div<{
  $progress: number;
}>`
  height: 5px;
  background: var(--border-secondary);
  border-radius: 100px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: ${({ $progress }) => `${$progress}%`};
    height: 100%;
    background: var(--textAndLabels-main);
    border-radius: 100px;
    transition: width 0.3s ease;
  }
`;

export const Title = styled(Typography)`
  width: auto;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const PayWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const PayButton = styled(Button)`
  width: 100%;
  margin-block: 16px;
  flex: 1;
  color: var(--bg-main);
`;
